import { Button } from 'components';
import { RecoveryPasswordForm } from 'components/Forms/Auth/RecoveryPasswordForm';
import { AuthTemplate } from 'components/templates/AuthTemplate';
import { useQueryParams } from 'hooks/useQuery';
import { useDisclose } from 'hooks/utilHooks';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { recoveryPasswordValidation } from 'schemas/authValidation';
import { AuthService } from 'services';
import { IRecoveryPassword, ISignIn, TitleByUserRole } from 'types/Auth';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material';

type Props = {
  firstPassword?: boolean;
};

export const RecoveryPasswordPage: FC<Props> = ({ firstPassword }) => {
  const { replace } = useHistory();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState('');
  const { isOpen, onClose, onOpen } = useDisclose();
  const { token } = useQueryParams<Pick<IRecoveryPassword, 'token'>>();

  const { userRole } = useParams<{ userRole: ISignIn['role'] }>();

  const form = useForm<IRecoveryPassword>({
    defaultValues: {
      password: '',
      passwordConfirmation: '',
      token,
      role: userRole,
    },
    resolver: yupResolver(recoveryPasswordValidation),
  });

  const title = TitleByUserRole[userRole];
  document.title = title;

  const onSubmit = async (values: IRecoveryPassword) => {
    try {
      setLoading(true);
      setError('');

      await AuthService.recoveryPasswordWithToken(values);
      onOpen();
    } catch (reason) {
      const { message } = reason as Error;
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    replace(`/${userRole}`);
  };

  return (
    <form>
      <FormProvider {...form}>
        <AuthTemplate
          title={title}
          error={error}
          loading={loading}
          userRole={userRole}
        >
          <RecoveryPasswordForm firstPassword={firstPassword}>
            <Button
              fullWidth
              sx={{ mb: 2 }}
              type="submit"
              onClick={form.handleSubmit(onSubmit)}
            >
              {firstPassword ? 'Definir senha' : 'Redefinir senha'}
            </Button>
          </RecoveryPasswordForm>
        </AuthTemplate>

        <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            {firstPassword ? 'Definição de senha' : 'Redefinição de senha'}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <DialogContentText>
              {firstPassword
                ? 'Senha definida com sucesso'
                : 'Senha redefinida com sucesso'}
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button variant="text" onClick={handleClose}>
              Entrar
            </Button>
          </DialogActions>
        </Dialog>
      </FormProvider>
    </form>
  );
};
